import { assert } from '@deqo/foundation';
import { shade } from 'polished';
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Arr } from '../../../CodesForFoundation';
import { LFC } from '../../../Legacy';
import { Component } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Fill } from '../../../ui/Fill';
import { useResolve } from '../../Container';
import { Counter } from '../../common/Users';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Audited } from '../../packages/auditing/Audited';
import { Concealable } from '../../packages/conceal/Concealable';
import { CreatedIncognitoAttr } from '../../packages/incognito/IncognitoAssociations';
import { Physical, RelativeTo } from '../entities/Physical';
import { Participant } from '../participants/Participant';
import { ParticipantManager } from '../participants/ParticipantManager';
import { Sebastian } from '../ui/Sebastian';
import { Colors } from './../../../ui';
import { AnimateHack } from './AnimateHack';
import { Emoticon } from './Emoticon';
import { GraffitiAttr } from './Graffiti';
import { Effect, Pose, PoseAttr } from './Pose';
import { RenderingContext } from './RenderingContext';

const popInOpacity = keyframes`
    0% { opacity: 0 }
    100% { opacity: 1 }
`;

const popInTransform = keyframes`
    0% { transform: scale(0.5) }
    100% { transform: scale(1.0) }
`;

const popInEffect = ({ duration }: Effect) => css`
    animation: 
        ${popInOpacity} ${duration}ms ease-out,
        ${popInTransform} ${duration}ms cubic-bezier(0,.99,.2,1.14)
    ; 
`;

const shakeTransform = keyframes`
    8%, 41% {
        transform: translateX(-10px);
    }
    25%, 58% {
        transform: translateX(10px);
    }
    75% {
        transform: translateX(-5px);
    }
    92% {
        transform: translateX(5px);
    }
    0%, 100% {
        transform: translateX(0);
    }
`;

const shakeEffect = ({ duration }: Effect) => css`
    animation: ${shakeTransform} ${duration}ms linear; 
`;

const flashTransform = (color: string) => keyframes`
    0% {
        opacity: 1;
        box-shadow: 0 0 0 0 ${color};
        border-radius: .125rem;
    }
    50%, 100% {
        opacity: 0;
        box-shadow: 0 0 0 12px ${color};
        border-radius: .25rem;
    }
`;

const flashEffect = ({ duration, rect, color }: Effect) => css`
    &:after {
        content: '';
        z-index: 100;
        position: absolute;
        left: ${rect.left}px;
        top: ${rect.top}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        animation: ${flashTransform(shade(0.25, color ?? Colors.primaryLight))} linear ${duration}ms;
        /* white-space: nowrap; */
        opacity: 1;
    }
    /* animation: ${shakeTransform} ${duration}ms linear;  */
`;

const textTransform = keyframes`
    0% {
        transform: translate(-50%, -100%);
        opacity: 1;
    }
    50% {
        transform: translate(-50%, -150%);
        opacity: 1;
    }
    100% {
        transform: translate(-50%, -200%);
        opacity  : 0;
    }
`;

const textEffect = ({ duration, value, position, color }: Effect) => css`
    &:after {
        content: '${value.toString()}';
        z-index: 100;
        position: absolute;
        left: ${!!position ? `${position.x}px` : '50%'};
        top: ${!!position ? `${position.y}px` : '0%'};
        transform: translate(-50%, -50%);
        padding: 4px 8px;
        border-radius: 8px;
        font-family: "Rubik";
        text-transform: uppercase;
        font-variant-numeric: tabular-nums;
        font-size: 24px;
        font-weight: 450;
        color: ${Colors.white};
        background: ${color ?? Colors.primaryLight};
        animation: ${textTransform} linear ${duration}ms;
        white-space: nowrap;
        opacity: 0;
    }
    /* animation: ${shakeTransform} ${duration}ms linear;  */
`;

const fadeFrames = (value: number) => keyframes`
    0% {
        opacity: 1;
    }
    100% {
        opacity: ${value};
    }
`;

const fadeAnimation = ({ duration, value }: Effect) => css`
    animation: ${fadeFrames(Number(value))} ${duration < 0 ? 100 : duration}ms linear; 
    animation-fill-mode: forwards;
`;

const raiseFrames = (value: number) => keyframes`
    0% {
        transform: scale(1);
    }
    100% {
        transform: scale(${value});
    }
`;

const raiseEffect = ({ duration, value }: Effect) => css`
    animation: ${raiseFrames(Number(value))} ${duration < 0 ? 100 : duration}ms linear; 
    animation-fill-mode: forwards;
    z-index: 1000;
`;

const tweenFrames = (from: Matrix, to: Matrix) => keyframes`
    0% {
        transform: ${from.toCSS()};
    }
    100% {
        opacity: ${to.toCSS()};
    }
`;

const tweenAnimation = ({ duration, from, to }: Effect) => css`
    animation: ${tweenFrames(from, to)} ${duration < 0 ? 100 : duration}ms ease-out; 
    animation-fill-mode: forwards;
`;

const computeShuffleAnimation = (value: number) => {
    const ov = new Point(30, 0).rotate(Math.PI * 2 * value);
    return keyframes`
        0% {
            transform: rotate(0deg) translate(0, 0px) rotate(0deg);
        }
        10% {
            transform: rotate(0deg) translate(${ov.x}px, ${ov.y}px) rotate(0deg);
        }
        70% {
            transform: rotate(270deg) translate(${ov.x}px, ${ov.y}px) rotate(-270deg);
        }
        90% {
            transform: rotate(360deg) translate(${ov.x / 2}px, ${ov.y / 2}px) rotate(-360deg);
        }
        100% {
            transform: rotate(360deg) translate(0, 0px) rotate(-360deg);
        }
    `;
};

const shuffleAnimations = new Array(12).fill(0).map((_, i) => computeShuffleAnimation(1 / 12 * i));

const shuffleEffect = ({ value }: Effect) => css`
    animation: ${(typeof (value) === 'number') ? Arr.at(shuffleAnimations, value) : 'none'} 1000ms linear;
    animation-direction: ${(value ?? 0) % 2 === 0 ? 'reverse' : 'forwards'};
`;

export type PoseAsProps = { pose: Pose };

const effectSupport = (name: string, definition: (eff: Effect) => any) =>
    ({ pose }: PoseAsProps) => {
        const eff = (pose.effects ?? {})[name];
        return !!eff ? definition(eff) : '';
    };

const Frame = styled.div<PoseAsProps & { animate?: boolean, layer?: number }>`
    position: absolute;
    left: 0;
    top: 0;
    z-index: ${p => p.layer ?? 1};
    transform-origin: left top;
    will-change: transform;
    ${p => p.animate ? 'transition: transform 100ms linear' : ''};
    ${effectSupport('tween', tweenAnimation)}
`;

export const RenderBounds: LFC<{ object: Physical, style?: any }> = ({ object, style, children }) => {
    style = style ?? {};

    const { type } = useContext(RenderingContext);
    const transform = type === 'default'
        ? object.transform(RelativeTo.Model).toCSS()
        : Matrix.identity.toCSS();

    return <Frame
        id={`_${object.id.replace(/\//g, '_')}`}
        data-type={`${object.type}Visual`}
        layer={1}
        pose={Pose.get(object)}
        animate={AnimateHack.animate}
        style={{ transform, ...style }}>
        {children}
    </Frame>;
};

export const RenderInversionBounds: LFC<{ object: Physical }> = ({ object, children }) => {
    const transform = object.transform(RelativeTo.Model).inverse().toCSS();
    return <Frame
        id={`_${object.id.replace(/\//g, '_')}`}
        layer={1}
        pose={Pose.get(object)}
        animate={AnimateHack.animate}
        style={{ transform }}>
        {children}
    </Frame>;
};

// export const RenderFrame2: LFC<{ object: Physical }> = ({ object, children }) => {
//     const transform = object.transform(RelativeTo.Model).toCSS();
//     const pose = Pose.get(object);
//     const animate = !pose.targeted && !pose.selected;
//     return <RenderFrameInner animate={animate} style={{ transform }}>
//         {children}
//     </RenderFrameInner>
// };

export const RenderBorder = styled.div<PoseAsProps>`
    overflow: hidden;
    ${effectSupport('shake', shakeEffect)}
    ${effectSupport('popIn', popInEffect)}
    ${effectSupport('text', textEffect)}
    ${effectSupport('flash', flashEffect)}
    ${effectSupport('fade', fadeAnimation)}
    ${effectSupport('raise', raiseEffect)}
`;

export const RenderEffect = styled.div<PoseAsProps>`
    ${effectSupport('shake', shakeEffect)}
    ${effectSupport('shuffle', shuffleEffect)}
    ${effectSupport('popIn', popInEffect)}
    ${effectSupport('text', textEffect)}
    ${effectSupport('flash', flashEffect)}
    ${effectSupport('raise', raiseEffect)}
`;

export const RenderTween = styled.div<PoseAsProps>`
    ${effectSupport('tween', tweenAnimation)}
`;

export const RenderInner: LFC<{ object: Physical }> = ({ object, children }) => {
    const { w, h } = object.size();
    return <RenderEffect pose={Pose.get(object)} style={{ width: w, height: h }}>
        {children}
    </RenderEffect>;
};

const fadeIn = keyframes`
    from { opacity: 0 }
    to { opacity: 1 }
`;

const GraffitiFrame = styled(Fill)`
    & > * {
        position: relative;
        width: 100%;
        height: 100%;
        /* animation: ${fadeIn} 150ms ease-out;
        animation-fill-mode: forwards; */
    }
`;

export const RenderGraffiti: LFC<{ object: Component }> = ({ object }) => {
    const [frame, setFrame] = useState<HTMLDivElement>();
    const graffiti = GraffitiAttr.get(object);
    useEffect(() => {
        if (!graffiti || !frame) return;
        if (graffiti.parentElement !== frame) graffiti.remove();
        frame.append(graffiti);
        return () => graffiti.remove();
    }, [frame, graffiti]);
    if (!graffiti) return null;
    return <GraffitiFrame ref={setFrame} />;
};

export const RenderHeadline = styled.div`
    position: absolute;
    top: 0px;
    left: 50%;
    transform: translate(-50%, -50%) translateY(0px) scale(1);
`;

export const RenderCounter: LFC<{ user: Participant }> = ({ user }) => {
    return <Counter size={32} unit="px" color={user.color} photo={user.photo}>
        {Participant.toInitials(user.name)}
    </Counter>;
};

const SebastianFrame = styled.div<{ concealed: boolean, hover: boolean, active: boolean }>`
    position: relative;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${p => p.active ? 'whitesmoke' : 'white'};
    box-shadow: 0 0 0 ${p => p.hover ? 2 : 1}px ${p => p.concealed ? Colors.negative : Colors.positive};

    ${Sebastian} {
        position: relative;
        top: -6px;
        left: -1px;
        width: 100%;
        height: 100%;
        transform: scale(1.1);
    }
`;

const sebastianAnimation = (strong: boolean) => keyframes`
    0% {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
    50% {
        opacity: 1;
    }
    100% {
        transform: translateY(-${!!strong ? 80 : 60}px) scale(${!!strong ? 3 : 2});
        opacity: 0;
    }
`;

const SebastianAnimated = styled.div<{ duration: number, strong: any }>`
    position: relative;
    width: 32px;
    height: 32px;

    animation: ${p => sebastianAnimation(p.strong)} ${p => p.duration}ms ease-out; 
    animation-fill-mode: forwards;

    ${Sebastian} {
        position: relative;
        top: -6px;
        left: -1px;
        width: 100%;
        height: 100%;
        transform: scale(1.1);
    }
`;

export const RenderConcealed: LFC<{ object: Concealable }> = ({ object }) => {
    const hover = Pose.hasInteraction(object, 'invoke', 'flip');
    const active = hover && Pose.get(object).pressed;
    return <SebastianFrame concealed={object.concealed} hover={hover} active={active}>
        <Sebastian stance={object.concealed ? 'private' : 'public'} />
    </SebastianFrame>;
};

export const RenderConcealedEffect: LFC<{ effect: Effect }> = ({ effect }) => {
    return effect && <RenderHeadline>
        <SebastianAnimated strong={effect.strong} duration={effect?.duration}>
            <Sebastian stance={effect?.value ? 'private' : 'public'} />
        </SebastianAnimated>
    </RenderHeadline>;
};

// const SebastianAnimated = styled.div<{ duration: number }>`
//     position: absolute;
//     top: 50%;
//     left: 50%;
//     width: 64px;
//     height: 64px;
//     margin-left: -${64 / 2}px;
//     margin-top: -${64 / 2}px;

//     animation: ${sebastianAnimation} ${p => p.duration}ms ease-out;
//     animation-fill-mode: forwards;

//     ${Sebastian} {
//         position: relative;
//         top: -6px;
//         left: -1px;
//         width: 100%;
//         height: 100%;
//         transform: scale(1.1);
//     }
// `;

export const RenderError = () => {
    return <Flexer background={Colors.negative} color="#fff">Error</Flexer>;
};

export const RenderBox: LFC<any> = ({ ref, children, ...props }) => {
    return <div style={{ position: 'absolute', ...props }}>{children}</div>;
};

export const RenderSvg: LFC<{ area: Rect, style?: any }> = ({ area, style, children }) => {
    return <svg style={{ position: 'absolute', ...area.toCSS(), ...(style ?? {}) }}>
        {children}
    </svg>;
};

export const usePose = (object: Physical) => {
    return PoseAttr.get(object);
};

export const usePoseEffect = (object: Physical, name: string) => {
    return Pose.getEffect(object, name);
};

export const usePressable = (object: Physical, name: string) => {
    const { interaction, pressed } = usePose(object);
    return (interaction?.type === 'invoke' && interaction.hint === name)
        ? pressed ? 'active' : 'hover'
        : 'idle';
};

export type PressableProps = { area: Rect, state: string };

export const usePressable2 = (object: Physical & { layout: any }, name: string) => {
    const { interaction, pressed } = usePose(object);
    assert(name in object.layout, 'usePressable: layout must contain entry corresponding to name');
    const entry = object.layout[name] as Rect | { area: Rect };
    const area = entry instanceof Rect ? entry : entry.area;
    assert(area instanceof Rect, 'usePressable: area must be a Rect');
    const state = (interaction?.type === 'invoke' && interaction.hint === name)
        ? pressed ? 'active' : 'hover'
        : 'idle';
    return { area, state };
};

const PressableContext = React.createContext<string>('');

export const usePressableContext = () => useContext(PressableContext);

export const RenderPressable = React.memo<{ object: Physical, hint: string, children?: ReactNode }>(({ object, hint, children }) => {
    const { interaction, pressed } = usePose(object);
    const state = (interaction?.type === 'invoke' && interaction.hint === hint)
        ? pressed ? 'active' : 'hover'
        : 'idle';
    return <PressableContext.Provider value={state}>
        {children}
    </PressableContext.Provider>;
});

export const useDragInteractable = (object: Physical & { layout: any }, name: string) => {
    const { interaction, pressed } = usePose(object);
    assert(name in object.layout, 'useDraggable: layout must contain entry corresponding to name');
    const area = object.layout[name] as Rect;
    const state = (interaction?.type === 'drag' && interaction.hint === name)
        ? pressed ? 'active' : 'hover'
        : 'idle';
    return { area, state };
};

export const RenderButton = React.memo<PressableProps & { children?: ReactNode }>(({ area, state, children }) => {

    const scaler = 1;

    const transform =
        (state === 'idle' ? Matrix.translate(0, -2 * scaler).toCSS() : null) ??
        (state === 'hover' ? Matrix.translate(0, -4 * scaler).toCSS() : null) ??
        (state === 'active' ? Matrix.translate(0, 0).toCSS() : null);

    const backStyle = {
        ...area.toCSS(),
        background: Colors.gray300,
        borderRadius: `${8 * scaler}px`,
    };

    const foreStyle = {
        ...area.toCSS(),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: `0 ${5 * scaler}px`,
        fontFamily: 'Rubik',
        fontSize: Math.round(area.height / 3),
        fontWeight: 500,
        fontColor: Colors.text,
        textAlign: 'center',
        lineHeight: '1.2em',
        background: Colors.gray200,
        borderRadius: `${8 * scaler}px`,
        transform,
        transition: 'transform 75ms ease-out',
    };

    return <>
        <RenderBox {...backStyle} />
        <RenderBox {...foreStyle}>
            {children}
        </RenderBox>
    </>;
});

export type BracketShortcut =
    { type: 'none' } |
    { type: 'emoticon', value: string } |
    { type: 'user', value: Participant } |
    { type: 'highlight', value: string } |
    { type: 'url', value: string };

export const useBracketShortcut = (value: string): BracketShortcut => {
    const pm = useResolve(ParticipantManager);
    return useMemo(() => {
        if (Emoticon.lookup(value)) {
            return { type: 'emoticon', value: Emoticon.lookup(value) };
        }
        if (value.match(/^\((FU|GU|US)\w{8,12}\)/i)) {
            const ptpt = pm.getOrLoad(value.slice(1, -1).toUpperCase());
            return {
                type: 'user',
                value: ptpt,
            };
        }
        if (value.match(/^\(https?:\/\/.+\)/i)) {
            return { type: 'url', value: value.slice(1, -1) };
        }
        if (value.match(/^\|\w+\|/i)) {
            return { type: 'highlight', value: value.slice(1, -1) };
        }
        return { type: 'none' };
    }, [value]);
};

export const useUser = (object: Audited) => {
    const pm = useResolve(ParticipantManager);
    const { userId } = object;
    const incog = CreatedIncognitoAttr.get(object);
    return useMemo(
        () => pm.get(userId) ?? (incog ? Participant.anonymous : Participant.unknown),
        [userId, incog]);
};

export const RenderEmoticon: LFC<{ value: string, size?: string | number } & Record<string, any>> = ({ value, size, ...rest }) => {
    return <RenderBox
        inset="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
        {...rest}>
        <Icon size={size} src={`deqo://icons/tokens/${value}@[0.75,0.75,0]`} />
    </RenderBox>;
};

const RenderImage = styled.div`
    display: block;
    border-radius: 8px;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
`;

const RenderIcon = ({ src }) => {
    return <Icon src={src} />;
};

const RenderInitials = styled.div<{ color: string }>`
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Rubik;
    font-size: 34px;
    font-weight: 450;
    color: white;
    background: ${p => p.color};
    border-radius: 50%;
`;

export const RenderUserInfo: LFC<{ value: Participant } & Record<string, any>> = ({ value, ...rest }) => {
    if (!value) return null;
    const style = { backgroundImage: `url(${value.photo})` };
    const { photo, color, name } = value;
    return <RenderBox inset="10px" display="flex" alignItems="center" justifyContent="center" {...rest}>
        {!photo && <RenderInitials color={color}>{Participant.toInitials(name)}</RenderInitials>}
        {!!photo && photo.startsWith('deqo://') && <RenderIcon src={photo} />}
        {!!photo && !photo.startsWith('deqo://') && <RenderImage style={style} />}
    </RenderBox>;
};

export const RenderUrl: LFC<{ value: string } & Record<string, any>> = ({ value, ...rest }) => {
    const style = { backgroundImage: `url(${value})` };
    return <RenderBox inset="10px" display="flex" alignItems="center" justifyContent="center" {...rest}>
        <RenderImage style={style} />
    </RenderBox>;
};
